import type { TxInsightsResponse } from "./api";

/**
 * Privacy-set arithmetic for the saved-view page, over the /txs/insights
 * breakdown. Pure so the tiering and the weighted median can be pinned by
 * tests rather than eyeballed in a chart.
 */

export type PrivacyTier = "outlier" | "small" | "set" | "unanalyzed";

/**
 * Same cut as the Privacy Sets table's size badge: an outlier (or a set of
 * one) is trivially identifiable, 2–5 is thin cover, 6+ is a real crowd.
 */
export const SMALL_SET_MAX = 5;

export function tierOf(clusterId: number, size: number): Exclude<PrivacyTier, "unanalyzed"> {
  if (clusterId === -1 || size <= 1) return "outlier";
  return size <= SMALL_SET_MAX ? "small" : "set";
}

export interface PrivacyBreakdown {
  /** Matching txs per tier; sums to insights.matching. */
  tiers: Record<PrivacyTier, number>;
  /** Real clusters (not the outlier bucket) holding at least one match. */
  setsTouched: number;
  /**
   * Median privacy-set size a matching tx sits in, over analyzed matches —
   * each tx weighs one, so a view mostly hiding in one big set reads big even
   * if it also grazes many small ones. Outliers count as sets of one. Null
   * when nothing is analyzed.
   */
  medianSetSize: number | null;
}

export function privacyBreakdown(insights: TxInsightsResponse): PrivacyBreakdown {
  const tiers: Record<PrivacyTier, number> = {
    outlier: 0,
    small: 0,
    set: 0,
    unanalyzed: insights.unanalyzed,
  };
  let setsTouched = 0;
  const weighted: Array<{ size: number; n: number }> = [];

  for (const c of insights.clusters) {
    const size = c.clusterId === -1 ? 1 : c.size;
    tiers[tierOf(c.clusterId, size)] += c.matching;
    if (c.clusterId !== -1) setsTouched++;
    weighted.push({ size, n: c.matching });
  }

  weighted.sort((a, b) => a.size - b.size);
  const total = weighted.reduce((sum, w) => sum + w.n, 0);
  let medianSetSize: number | null = null;
  if (total > 0) {
    // Lower median: the size of the ceil(total/2)-th tx in size order.
    const target = Math.ceil(total / 2);
    let seen = 0;
    for (const w of weighted) {
      seen += w.n;
      if (seen >= target) {
        medianSetSize = w.size;
        break;
      }
    }
  }

  return { tiers, setsTouched, medianSetSize };
}

/**
 * Colours for privacy sets selected on the saved-view page, by selection
 * slot — never by cluster id, whose mod-10 badge colours collide. The first
 * three slots of the dataviz reference palette's dark steps: they clear the
 * CVD and normal-vision floors for every pair at once (validated against the
 * #12121a card surface), which a fourth slot would not — hence the cap.
 */
export const SELECTION_COLORS = ["#3987e5", "#d95926", "#199e70"] as const;
export const MAX_SELECTED_SETS = SELECTION_COLORS.length;

/** A selected set's display name: outliers are a bucket, not a cluster. */
export const setLabel = (clusterId: number) =>
  clusterId === -1 ? "Outliers" : `Cluster ${clusterId}`;

/**
 * Stacked segments inside one histogram bar, as { bottom, h } in percent of
 * the plot height. Bars are sqrt-scaled (bar height = sqrt(count) / sqrt(max)),
 * and the stack sits on that same axis: segment k spans sqrt(cumulative count
 * before it) to sqrt(cumulative count through it). Sized linearly inside the
 * bar instead, a set of a few hundred txs under a bucket of thirty thousand
 * would be a sliver — sharing the bars' axis keeps it visible and keeps the
 * whole chart on one scale. Parts are disjoint subsets of the bucket, so the
 * cumulative count (and the stack) never passes the bar.
 */
export function stackOnSqrtAxis(
  parts: number[],
  bucketTotal: number,
  sqrtMax: number,
): Array<{ bottom: number; h: number }> {
  const at = (n: number) => (sqrtMax > 0 ? (Math.sqrt(Math.min(n, bucketTotal)) / sqrtMax) * 100 : 0);
  let cum = 0;
  return parts.map((c) => {
    const bottom = at(cum);
    cum += c;
    return { bottom, h: at(cum) - bottom };
  });
}
